/**
 * notaFiscal.js — helper único de Nota Fiscal(is) vinculada(s) à POC.
 *
 * Modelo ATUAL (múltiplas NFs), guardado em `req.compra.poc.notasFiscais`:
 *   [
 *     {
 *       numero: '000123',
 *       dataEmissao: '2026-05-20',        // ISO yyyy-mm-dd
 *       valor: 2654.50,                   // número
 *       fornecedor: 'TOTVS S.A.',
 *       arquivo: { nome, tipo, base64, tamanho }   // reusa o padrão dos anexos
 *     },
 *     ...
 *   ]
 *
 * Compatibilidade (lida, nunca mais escrita):
 *   - `req.compra.poc.notaFiscal` (objeto único) — formato anterior à migração
 *     para múltiplas NFs.
 *   - `req.compra.notaFiscal` (objeto único, só o arquivo) — formato legado
 *     mais antigo, anterior à estruturação em número/data/valor.
 * A leitura aqui normaliza os três formatos para a mesma forma de lista, então
 * toda a UI consome `getNotasFiscais(req)` sem se preocupar com a origem.
 */

/** Normaliza o arquivo da NF (aceita `type` ou `tipo`). */
function normArquivo(a) {
  if (!a || !a.base64) return null;
  return {
    nome: a.nome || a.fileName || 'nota_fiscal',
    tipo: a.tipo || a.type || 'application/octet-stream',
    base64: a.base64,
    tamanho: a.tamanho || a.size || 0,
  };
}

/** Normaliza um objeto de NF (número/data/valor/fornecedor/arquivo). */
function normNf(nf) {
  if (!nf) return null;
  return {
    numero: nf.numero ? String(nf.numero) : '',
    dataEmissao: nf.dataEmissao || '',
    valor: nf.valor != null ? Number(nf.valor) : null,
    fornecedor: nf.fornecedor || '',
    // o arquivo pode estar em .arquivo ou (tolerância) direto no objeto
    arquivo: normArquivo(nf.arquivo) || normArquivo(nf),
  };
}

/**
 * Retorna TODAS as NFs normalizadas de uma requisição (array, possivelmente
 * vazio). Forma de cada item: { numero, dataEmissao, valor, fornecedor, arquivo|null }.
 */
export function getNotasFiscais(req) {
  if (!req) return [];
  const poc = req.compra?.poc || {};

  // Formato atual: array
  if (Array.isArray(poc.notasFiscais) && poc.notasFiscais.length > 0) {
    return poc.notasFiscais
      .map(normNf)
      .filter(nf => nf && (nf.numero || nf.arquivo));
  }

  // Formato anterior: objeto único em poc.notaFiscal
  const nfNova = poc.notaFiscal;
  if (nfNova && typeof nfNova === 'object' && (nfNova.numero || nfNova.arquivo || nfNova.base64)) {
    const nf = normNf(nfNova);
    return nf ? [nf] : [];
  }

  // Formato legado mais antigo: compra.notaFiscal = só o arquivo
  const nfLegada = req.compra?.notaFiscal;
  if (nfLegada && (nfLegada.base64 || nfLegada.nome)) {
    const nf = normNf(nfLegada);
    return nf ? [nf] : [];
  }

  return [];
}

/**
 * Retorna a NF "principal" normalizada de uma requisição (a primeira da
 * lista), ou null se não houver nenhuma. Mantido para compatibilidade com
 * código que ainda trabalha com uma única NF (ex.: exibição resumida).
 */
export function getNotaFiscal(req) {
  return getNotasFiscais(req)[0] || null;
}

/** true se a requisição tem ao menos uma NF vinculada (qualquer formato). */
export function temNotaFiscal(req) {
  return getNotasFiscais(req).length > 0;
}

/** Número da NF principal (string) ou '' — usado em indicadores simples. */
export function numeroNotaFiscal(req) {
  return getNotaFiscal(req)?.numero || '';
}

/**
 * Casa um termo de busca contra o número de QUALQUER NF da requisição
 * (comparação tolerante a zeros à esquerda e espaços). Ex.: "123" casa "000123".
 * Retorna boolean.
 */
export function matchNotaFiscal(req, termo) {
  const t = String(termo || '').trim().toLowerCase();
  if (!t) return false;
  const stripZ = (s) => s.replace(/^0+/, '');
  const tStripped = stripZ(t);
  if (!tStripped) return false;
  return getNotasFiscais(req).some(nf => {
    const num = (nf.numero || '').toLowerCase();
    if (!num) return false;
    if (num.includes(t)) return true;
    return stripZ(num).includes(tStripped);
  });
}
